import type {
  KeyboardEngineState,
  KeyboardEventSubscriber,
  KeyboardHandlerOptions,
  NormalizedKeyEvent,
  Platform,
} from './types';

// ─── Key Normalization ────────────────────────────────────────────────────────

/** Maps `KeyboardEvent.key` values to canonical lowercase strings. */
const KEY_NORMALIZE_MAP: Readonly<Record<string, string>> = {
  Escape: 'escape',
  Enter: 'enter',
  Tab: 'tab',
  ' ': 'space',
  Space: 'space',
  Backspace: 'backspace',
  Delete: 'delete',
  ArrowUp: 'arrowup',
  ArrowDown: 'arrowdown',
  ArrowLeft: 'arrowleft',
  ArrowRight: 'arrowright',
  PageUp: 'pageup',
  PageDown: 'pagedown',
  Home: 'home',
  End: 'end',
  Insert: 'insert',
  CapsLock: 'capslock',
  NumLock: 'numlock',
  ScrollLock: 'scrolllock',
  PrintScreen: 'printscreen',
  ContextMenu: 'contextmenu',
  // Modifier keys (usually filtered out but normalize anyway)
  Control: 'control',
  Meta: 'meta',
  Alt: 'alt',
  Shift: 'shift',
};

function normalizeKey(key: string): string {
  const mapped = KEY_NORMALIZE_MAP[key];
  if (mapped) return mapped;
  // Single characters: lowercase
  if (key.length === 1) return key.toLowerCase();
  // Function keys (F1–F24): already 'F1' etc., lowercase
  return key.toLowerCase();
}

// ─── Platform Detection ───────────────────────────────────────────────────────

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'linux';
  const p = navigator.platform.toLowerCase();
  if (p.includes('mac') || p.includes('iphone') || p.includes('ipad')) return 'mac';
  if (p.includes('win')) return 'windows';
  return 'linux';
}

const PLATFORM: Platform = detectPlatform();

// ─── Combo String ─────────────────────────────────────────────────────────────

/**
 * Build a canonical combo string from key + modifier flags.
 * Modifiers always appear in the order: ctrl → alt → shift → key.
 *
 * @example
 * buildCombo('k', { ctrl: true })           → 'ctrl+k'
 * buildCombo('enter', { shift: true })      → 'shift+enter'
 * buildCombo('s', { ctrl: true, shift: true }) → 'ctrl+shift+s'
 */
export function buildCombo(
  key: string,
  modifiers?: { ctrl?: boolean; alt?: boolean; shift?: boolean },
): string {
  const parts: string[] = [];
  if (modifiers?.ctrl) parts.push('ctrl');
  if (modifiers?.alt) parts.push('alt');
  if (modifiers?.shift) parts.push('shift');
  parts.push(key);
  return parts.join('+');
}

// ─── Target Context Helpers ───────────────────────────────────────────────────

function resolveTargetContext(target: EventTarget | null): {
  inTextInput: boolean;
  inButton: boolean;
  inContentEditable: boolean;
} {
  const el = target instanceof HTMLElement ? target : null;
  if (!el) return { inTextInput: false, inButton: false, inContentEditable: false };

  const tag = el.tagName.toLowerCase();
  const type = ((el as HTMLInputElement).type ?? '').toLowerCase();

  const inTextInput =
    (tag === 'input' &&
      type !== 'checkbox' &&
      type !== 'radio' &&
      type !== 'submit' &&
      type !== 'button') ||
    tag === 'textarea' ||
    tag === 'select' ||
    el.isContentEditable;

  const inButton =
    tag === 'button' || tag === 'a' || type === 'submit' || type === 'button' || type === 'reset';

  const inContentEditable = el.isContentEditable;

  return { inTextInput, inButton, inContentEditable };
}

// ─── Internal handler record ──────────────────────────────────────────────────

interface InternalHandler {
  readonly id: string;
  readonly priority: number;
  readonly handle: KeyboardHandlerOptions['handle'];
  readonly context: KeyboardHandlerOptions['context'];
  readonly description: string | undefined;
  readonly seq: number;
  enabled: boolean;
}

let _globalSeq = 0;

// ─── KeyboardEngine ───────────────────────────────────────────────────────────

/**
 * Central keyboard event pipeline.
 *
 * Responsibilities:
 *  - Normalize raw `KeyboardEvent`s into consistent `NormalizedKeyEvent`s.
 *  - Dispatch normalized events to registered handlers in priority order.
 *  - Support pause/resume to block ALL keyboard handling (e.g. during modals
 *    from external libraries, full-screen transitions, or loading screens).
 *  - Provide event history and subscriber hooks for debug tooling.
 *  - Register/unregister/enable/disable individual handlers at runtime.
 *
 * Architecture note:
 *  The `KeyboardEngineProvider` registers its `keydown` listener BEFORE all
 *  other ERP Core managers (ShortcutManager, ModalManager, etc.) because it is
 *  the outermost React provider. When the engine is paused it calls
 *  `event.stopImmediatePropagation()`, preventing ALL subsequent capture-phase
 *  listeners from firing — including the ShortcutManager and ModalManager.
 *
 *  When NOT paused, a handler returning `true` stops the engine's own pipeline
 *  but does NOT prevent ShortcutManager / ModalManager from running. The handler
 *  must explicitly call `event.native.stopImmediatePropagation()` to block them.
 *
 * This class has zero React imports — testable in isolation.
 */
export class KeyboardEngine {
  private readonly _handlers = new Map<string, InternalHandler>();
  private readonly _subscribers = new Set<KeyboardEventSubscriber>();
  private readonly _history = new Array<NormalizedKeyEvent>();

  private _isPaused = false;
  private _isDebug = false;

  private static readonly MAX_HISTORY = 50;

  // ─── Registration ──────────────────────────────────────────────────────────

  /**
   * Register a keyboard handler. Returns an unsubscribe function.
   *
   * Handlers with the same `id` are replaced (idempotent re-registration —
   * safe to call in React effects with deps).
   */
  register(options: KeyboardHandlerOptions): () => void {
    const internal: InternalHandler = {
      id: options.id,
      priority: options.priority,
      handle: options.handle,
      context: options.context,
      description: options.description,
      enabled: options.enabled ?? true,
      seq: ++_globalSeq,
    };
    this._handlers.set(options.id, internal);
    return () => this.unregister(options.id);
  }

  /** Remove a handler by ID. No-op if not registered. */
  unregister(id: string): void {
    this._handlers.delete(id);
  }

  enable(id: string): void {
    const h = this._handlers.get(id);
    if (h) h.enabled = true;
  }

  disable(id: string): void {
    const h = this._handlers.get(id);
    if (h) h.enabled = false;
  }

  toggle(id: string, enabled: boolean): void {
    if (enabled) {
      this.enable(id);
    } else {
      this.disable(id);
    }
  }

  isRegistered(id: string): boolean {
    return this._handlers.has(id);
  }

  // ─── Pause / Resume ────────────────────────────────────────────────────────

  /**
   * Pause ALL keyboard processing.
   *
   * While paused, the engine's `dispatch()` calls
   * `event.stopImmediatePropagation()` on every keydown event — preventing
   * every other capture-phase listener (ShortcutManager, ModalManager, etc.)
   * from receiving the event.
   *
   * Use cases:
   *  - A full-screen loading overlay is shown.
   *  - A third-party modal library that doesn't integrate with ERP Core.
   *  - Critical async operations where keyboard shortcuts should be disabled.
   */
  pause(): void {
    this._isPaused = true;
  }

  /** Resume normal keyboard processing after `pause()`. */
  resume(): void {
    this._isPaused = false;
  }

  isPaused(): boolean {
    return this._isPaused;
  }

  // ─── Debug Mode ────────────────────────────────────────────────────────────

  /**
   * Enable debug mode.
   *
   * In debug mode:
   *  - Every keydown event is logged to the console.
   *  - Events are stored in an in-memory history buffer (max 50 events).
   *  - Subscribers are notified on every event for monitoring UIs.
   */
  setDebugMode(enabled: boolean): void {
    this._isDebug = enabled;
    if (!enabled) this._history.length = 0;
  }

  isDebugMode(): boolean {
    return this._isDebug;
  }

  // ─── Dispatch ──────────────────────────────────────────────────────────────

  /**
   * Main dispatch entry point. Called by the `keydown` listener in
   * `KeyboardEngineProvider`.
   *
   * Dispatch flow:
   * 1. Normalize the raw `KeyboardEvent`.
   * 2. If paused: `stopImmediatePropagation` + `preventDefault`. Nothing fires.
   * 3. Sort handlers: priority DESC, then registration seq DESC (LIFO).
   * 4. Walk handlers in order. Skip disabled and context-mismatched handlers.
   * 5. If a handler returns `true`: stop the internal pipeline.
   *    (ShortcutManager still fires unless the handler also calls
   *    `event.native.stopImmediatePropagation()`.)
   * 6. Notify subscribers (debug mode and monitoring hooks).
   */
  dispatch(event: KeyboardEvent): void {
    if (!event.isTrusted) return;

    const normalized = this.normalize(event);

    if (this._isDebug) {
      this._recordHistory(normalized);
      console.warn(
        `[KeyboardEngine] ${normalized.combo}`,
        `(${normalized.inTextInput ? 'text-input' : 'default'})`,
        normalized.native.target,
      );
    }

    // ── Paused: block EVERYTHING ───────────────────────────────────────────
    if (this._isPaused) {
      event.stopImmediatePropagation();
      event.preventDefault();
      this._notifySubscribers(normalized, false, null);
      return;
    }

    // ── Build sorted handler list ──────────────────────────────────────────
    const sorted = [...this._handlers.values()].sort((a, b) =>
      b.priority !== a.priority ? b.priority - a.priority : b.seq - a.seq,
    );

    let consumed = false;
    let consumerId: string | null = null;

    // ── Dispatch pipeline ─────────────────────────────────────────────────
    for (const handler of sorted) {
      if (!handler.enabled) continue;

      // Context filter
      const ctx = handler.context;
      if (ctx) {
        if (ctx.inTextInput === true && !normalized.inTextInput) continue;
        if (ctx.inTextInput === false && normalized.inTextInput) continue;
        if (ctx.inButton === true && !normalized.inButton) continue;
        if (ctx.inButton === false && normalized.inButton) continue;
      }

      const result = handler.handle(normalized);

      if (result === true) {
        consumed = true;
        consumerId = handler.id;
        break; // Stop internal pipeline; ShortcutManager still fires
      }
    }

    this._notifySubscribers(normalized, consumed, consumerId);
  }

  // ─── Normalization ─────────────────────────────────────────────────────────

  /**
   * Convert a raw `KeyboardEvent` to a `NormalizedKeyEvent`.
   * Can be called directly without going through `dispatch()`.
   */
  normalize(event: KeyboardEvent): NormalizedKeyEvent {
    const key = normalizeKey(event.key);
    const isMac = PLATFORM === 'mac';

    // On Mac, Cmd (Meta) is the primary modifier — aliased to `ctrl`
    const ctrl = isMac ? event.ctrlKey || event.metaKey : event.ctrlKey;
    const meta = event.metaKey;
    const alt = event.altKey;
    const shift = event.shiftKey;
    const combo = buildCombo(key, { ctrl, alt, shift });

    const { inTextInput, inButton, inContentEditable } = resolveTargetContext(event.target);

    return {
      key,
      code: event.code,
      combo,
      ctrl,
      meta,
      alt,
      shift,
      platform: PLATFORM,
      inTextInput,
      inButton,
      inContentEditable,
      timestamp: Date.now(),
      native: event,
    };
  }

  // ─── Subscriptions ─────────────────────────────────────────────────────────

  /**
   * Subscribe to every dispatched event.
   * Returns an unsubscribe function.
   *
   * Subscribers receive events regardless of whether the engine is paused
   * or a handler consumed the event — useful for monitoring and debug UIs.
   * Note: subscribers are NOT called when `isTrusted === false`.
   */
  subscribe(listener: KeyboardEventSubscriber): () => void {
    this._subscribers.add(listener);
    return () => {
      this._subscribers.delete(listener);
    };
  }

  // ─── History ───────────────────────────────────────────────────────────────

  /**
   * Return the recorded event history (populated only in debug mode).
   * Maximum 50 most recent events.
   */
  getHistory(): readonly NormalizedKeyEvent[] {
    return this._history;
  }

  clearHistory(): void {
    this._history.length = 0;
  }

  // ─── State Snapshot ────────────────────────────────────────────────────────

  getState(): KeyboardEngineState {
    return {
      isPaused: this._isPaused,
      isDebugMode: this._isDebug,
      handlerCount: this._handlers.size,
    };
  }

  // ─── Static Utilities ──────────────────────────────────────────────────────

  /**
   * Build a canonical combo string from a key name and modifier flags.
   * Useful for static combo string definitions without a live event.
   *
   * @example
   * KeyboardEngine.combo('k', { ctrl: true }) → 'ctrl+k'
   */
  static combo(
    key: string,
    modifiers?: { ctrl?: boolean; alt?: boolean; shift?: boolean },
  ): string {
    return buildCombo(normalizeKey(key), modifiers);
  }

  // ─── Debug ─────────────────────────────────────────────────────────────────

  debug(): void {
    const sorted = [...this._handlers.values()].sort((a, b) => b.priority - a.priority);
    console.warn('[KeyboardEngine] state:', this.getState());
    console.warn(
      '[KeyboardEngine] handlers:',
      sorted.map((h) => ({
        id: h.id,
        priority: h.priority,
        enabled: h.enabled,
        desc: h.description,
      })),
    );
  }

  // ─── Private ───────────────────────────────────────────────────────────────

  private _recordHistory(event: NormalizedKeyEvent): void {
    if (!this._isDebug) return;
    this._history.push(event);
    if (this._history.length > KeyboardEngine.MAX_HISTORY) {
      this._history.shift();
    }
  }

  private _notifySubscribers(
    event: NormalizedKeyEvent,
    consumed: boolean,
    handlerId: string | null,
  ): void {
    for (const subscriber of this._subscribers) {
      subscriber(event, consumed, handlerId);
    }
  }
}
